import axios from 'axios'
import { lineItemType, repCodeType, rejectionType, shipToType, statusDetailType } from './types'
import { tempFiles } from './NewOrder/Correction'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Autocomplete, Button, Chip, CircularProgress, debounce, SxProps, TextField, Typography } from '@mui/material'
import { Dayjs } from 'dayjs'
import { DatePicker as MuiDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Check, Close, DoDisturb, PauseCircleOutline } from '@mui/icons-material'
import './helper.css'

export function getRepCodeName(repCode: repCodeType | undefined) {
  if (!repCode) return ''

  return [
    repCode.Engineer?.split(' ')[0],
    repCode.AccountExecutive?.split(' ')[0],
    repCode.InsideSalesRep.split(' ')[0],
  ]
    .filter(Boolean)
    .join(', ')
}

export function sortKits(lineItems: lineItemType[]) {
  return [...new Set(lineItems.map(line => line.Kit))].sort((a, b) => {
    const aLine = lineItems
      .filter(kit => kit.Kit === a && (kit.Number ?? 0) > 0)
      .reduce((min, line) => Math.min(min, line.Number ?? Number.MAX_VALUE), Number.MAX_VALUE)
    const bLine = lineItems
      .filter(kit => kit.Kit === b && (kit.Number ?? 0) > 0)
      .reduce((min, line) => Math.min(min, line.Number ?? Number.MAX_VALUE), Number.MAX_VALUE)
    return aLine - bLine
  })
}

export function loadDocs(id: string): Promise<tempFiles> {
  return new Promise((resolve, reject) => {
    axios
      .get(`/listDocuments/${id}`)
      .then(response => {
        const files: { [index: string]: DataTransfer } = {
          GovernmentAward: new DataTransfer(),
          IMPRESQuote: new DataTransfer(),
          VendorQuote: new DataTransfer(),
          RelatedDoc: new DataTransfer(),
        }

        const promises = []

        for (const docType in response.data) {
          for (const doc of response.data[docType]) {
            const docNameUrl = (doc.name as string).replaceAll('#', '%23')
            promises.push(
              axios
                .get(`/sharepointContents/${id}/${docType}/${docNameUrl}`, {
                  responseType: 'blob',
                })
                .then(response => {
                  if (response.status !== 201) {
                    files[docType].items.add(new File([response.data], doc.name))
                  }
                })
                .catch(err => {
                  if (err.response) {
                    // received error code other than 2xx
                    console.error(err.response)
                  } else if (err.request) {
                    // no response received
                    console.error(err.request)
                  } else {
                    // request was not made
                    console.error(err.message)
                  }
                  throw err
                }),
            )
          }
        }

        Promise.all(promises)
          .then(() => {
            resolve(files as tempFiles)
          })
          .catch(err => {
            reject(err)
          })
      })
      .catch(err => {
        if (err.response) {
          // received error code other than 2xx
          console.log(err.response)
        } else if (err.request) {
          // no response received
          console.log(err.request)
        } else {
          // request was not made
          console.error(err.message)
        }
      })
  })
}

export function CurrencyInput(props: {
  value: number | null
  onChange: (value: number | null) => void
  label?: string
  required?: boolean
  type?: string
  size?: 'small' | 'medium'
  fullWidth?: boolean
  disabled?: boolean
  decimals?: number
}) {
  const [cursor, setCursor] = useState<number | null>(0)
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    ref.current?.setSelectionRange(cursor, cursor)
  }, [ref, cursor, props.value])

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? null : e.target.value.replace('$', '').replaceAll(',', '')
    setCursor(e.target.selectionStart)

    if (!e.target.value || e.target.value === '$') {
      props.onChange(null)
      setCursor(1)
    } else if (!isNaN(Number(value))) {
      props.onChange(Number(Number(value).toFixed(props.decimals ?? 2)))

      if (
        e.target.value.length + 1 ===
        Number(value).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: props.decimals ?? 2,
        }).length
      ) {
        setCursor(prev => (prev ?? 0) + 1)
      }
    }
  }

  return (
    <TextField
      inputRef={ref}
      label={props.label}
      value={
        props.value === null
          ? '$'
          : props.value.toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: props.decimals ?? 2,
            })
      }
      onChange={onChange}
      required={props.required}
      size={props.size}
      fullWidth={props.fullWidth}
      disabled={props.disabled}
    />
  )
}

export function calculateGp(orderTotal: number, totalCost: number, purchaseVehicle: string, isCC: boolean = false) {
  return Number(
    (
      orderTotal -
      (isCC ? orderTotal * 0.03 : 0) -
      (purchaseVehicle === '2GIT' ? orderTotal * 0.0075 : 0) -
      (purchaseVehicle === 'CIO-CS' ? orderTotal * 0.0035 : 0) -
      (purchaseVehicle === 'GSA' ? orderTotal * 0.0075 : 0) -
      (purchaseVehicle.includes('SEWP') ? orderTotal * 0.01 : 0) -
      (purchaseVehicle === 'DOL BPA' ? orderTotal * 0.0075 : 0) -
      totalCost
    ).toFixed(2),
  )
}

export function DatePicker(props: {
  label?: string
  sx?: SxProps
  size?: 'small' | 'medium'
  value?: Dayjs | null
  disabled?: boolean
  className?: string
  fullWidth?: boolean
  onChange?: (date: Dayjs | null) => void
}) {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MuiDatePicker
        timezone="UTC"
        label={props.label}
        sx={props.sx}
        slotProps={{ textField: { size: props.size, className: props.className, fullWidth: props.fullWidth } }}
        value={props.value}
        onChange={props.onChange}
        disabled={props.disabled}
      />
    </LocalizationProvider>
  )
}

export function StatusChip(props: { status: string }) {
  return (
    <Chip
      label={props.status}
      size="small"
      color={
        props.status === 'Completed'
          ? 'success'
          : props.status === 'Hold - Sales' || props.status === 'Rejected - Corrected'
          ? 'warning'
          : props.status === 'Rejected' || props.status === 'Canceled'
          ? 'error'
          : 'info'
      }
      icon={
        props.status === 'Completed' ? (
          <Check />
        ) : props.status === 'Hold - Sales' || props.status === 'Hold - Back Office' ? (
          <PauseCircleOutline />
        ) : props.status.includes('Rejected') ? (
          <Close />
        ) : props.status === 'Canceled' ? (
          <DoDisturb />
        ) : undefined
      }
    />
  )
}

export function NumberField(props: {
  label: string
  size?: 'small' | 'medium'
  value?: number | null
  onChange?: (value: number | null) => void
  fullWidth?: boolean
  className?: string
}) {
  return (
    <TextField
      fullWidth={props.fullWidth}
      label={props.label}
      className={props.className}
      size={props.size}
      value={props.value ?? ''}
      onChange={e => {
        if (e.target.value.match(/^[\d]+$/)) {
          props.onChange?.(Number(e.target.value))
        } else if (e.target.value === '') {
          props.onChange?.(null)
        }
      }}
    />
  )
}

export function isAddressEmpty(address: shipToType | lineItemType) {
  return [
    address.ShipToAttention,
    address.ShipToCompany,
    address.ShipToAddress1,
    address.ShipToAddress2,
    address.ShipToAddressCity,
    address.ShipToAddressState,
    address.ShipToAddressZip,
    address.ShipToAddressCountry,
    address.ReceiverContactName,
    address.ReceiverContactEmail,
    address.ReceiverContactPhone,
  ].every(value => !value)
}

export function formatAddress(address: shipToType | lineItemType): string {
  const shipToLine = [
    address.ShipToAttention,
    address.ShipToCompany,
    address.ShipToAddress1,
    address.ShipToAddress2,
    address.ShipToAddressCity,
    address.ShipToAddressState,
    address.ShipToAddressZip,
    address.ShipToAddressCountry,
  ]
    .filter(value => value)
    .join(' ')

  const receiverLine = [address.ReceiverContactName, address.ReceiverContactEmail, address.ReceiverContactPhone]
    .filter(value => value)
    .join(' ')

  return [shipToLine, receiverLine].filter(Boolean).join('\n')
}

export function NetsuiteSearch({
  table,
  value,
  onChange,
}: {
  table: 'customer' | 'vendor'
  value: string | null
  onChange: (value: string | null) => void
}) {
  const [searchResults, setSearchResults] = useState<string[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchFilter, setSearchFilter] = useState('')

  const searchFilterRef = useRef(searchFilter)
  const handleSearch = useMemo(
    () =>
      debounce(() => {
        setSearchLoading(true)
        axios
          .get(`/netsuite/${table}List?filter=${searchFilterRef.current}`)
          .then(res => {
            setSearchResults(res.data.items.map((item: { companyname: string }) => item.companyname))
          })
          .catch(err => {
            console.error(err)
          })
          .finally(() => {
            setSearchLoading(false)
          })
      }, 500),
    [],
  )

  useEffect(() => {
    searchFilterRef.current = searchFilter
  }, [searchFilter])

  return (
    <Autocomplete
      fullWidth
      options={searchResults}
      value={value}
      onChange={(_, value) => onChange(value ?? null)}
      size="small"
      filterOptions={x => x}
      inputValue={searchFilter}
      loading={searchLoading}
      onInputChange={(_, value, reason) => {
        setSearchFilter(value)
        if (reason !== 'selectOption' && reason !== 'reset') {
          handleSearch()
        }
      }}
      noOptionsText={!!searchFilter ? 'No options' : 'Start typing to search'}
      renderInput={params => (
        <TextField
          {...params}
          label={table === 'customer' ? 'Customer Name' : 'Netsuite Vendor'}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: searchLoading ? <CircularProgress size={20} /> : params.InputProps.endAdornment,
            },
          }}
        />
      )}
    />
  )
}

export function Expandable(props: {
  text: string | null
  maxHeight?: number
  hideExpandable?: boolean
  gap?: number
  expandButtonInline?: boolean
  bulkExpand?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [hasOverflowed, setHasOverflowed] = useState(false)
  const textRef = useRef<HTMLDivElement>(null)

  const lines = props.text?.split('\n') || []
  const inlineButtonRow = Boolean(props.expandButtonInline && !props.hideExpandable)

  useEffect(() => {
    setExpanded(props.bulkExpand ?? false)
  }, [props.bulkExpand])

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current && props.maxHeight && props.maxHeight !== -1 && props.text) {
        const hasExplicitLineBreaks = lines.length > props.maxHeight

        const scrollHeight = textRef.current.scrollHeight
        const clientHeight = textRef.current.clientHeight
        const isActuallyOverflowing = scrollHeight > clientHeight

        const shouldShowButton = hasExplicitLineBreaks || isActuallyOverflowing

        if (shouldShowButton && !hasOverflowed) {
          setHasOverflowed(true)
        } else if (!shouldShowButton && hasOverflowed && !expanded) {
          setHasOverflowed(false)
        }

        setIsOverflowing(shouldShowButton || (hasOverflowed && expanded))
      }
    }

    checkOverflow()
    setTimeout(checkOverflow, 100)
    setTimeout(checkOverflow, 500)

    window.addEventListener('resize', checkOverflow)

    return () => {
      window.removeEventListener('resize', checkOverflow)
    }
  }, [props.text, props.maxHeight, expanded, lines.length, hasOverflowed])

  const textBlock = (
    <div
      ref={textRef}
      className={`responsive-expandable-text ${expanded ? 'expanded' : 'collapsed'}`}
      style={{
        gap: props.gap ? `${props.gap}px` : '0px',
        maxHeight: expanded
          ? 'none'
          : props.maxHeight
          ? props.maxHeight === -1
            ? 'none'
            : `${props.maxHeight * 1.43}em`
          : '8.5em',
      }}
    >
      {lines.map((line, index) => (
        <Typography key={index} variant="body2">
          {line}
        </Typography>
      ))}
    </div>
  )

  const expandButton =
    !props.hideExpandable && props.text && props.maxHeight !== -1 && isOverflowing ? (
      <Button
        size="small"
        className={`responsive-expandable-button${inlineButtonRow ? ' responsive-expandable-button-inline' : ''}`}
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
          setExpanded(!expanded)
        }}
      >
        {expanded ? 'See Less' : 'See More'}
      </Button>
    ) : null

  return (
    <div
      className={`responsive-expandable-container${inlineButtonRow ? ' responsive-expandable-container-inline' : ''}`}
      style={{ gap: inlineButtonRow ? 0 : props.gap ? `${props.gap}px` : '4px' }}
    >
      {inlineButtonRow ? (
        <div className="responsive-expandable-inline">
          {textBlock}
          {expandButton}
        </div>
      ) : (
        <>
          {textBlock}
          {expandButton}
        </>
      )}
    </div>
  )
}

export function buildStatusDetail(
  status: string,
  statusComment: string | null,
  rejection: rejectionType | null,
): statusDetailType | null {
  switch (status) {
    case 'Rejected':
      if (!rejection) {
        return null
      }
      return {
        label: 'Rejected',
        chipColor: 'error',
        reason: rejection.Code,
        comments: rejection.Comments,
        commentsKey: 'rejection',
      }
    case 'Canceled':
      return {
        label: 'Canceled',
        chipColor: 'error',
        reason: null,
        comments: statusComment,
        commentsKey: 'cancelled',
      }
    case 'Hold - Sales':
      return {
        label: 'Hold - Sales',
        chipColor: 'warning',
        reason: null,
        comments: statusComment,
        commentsKey: 'holdSales',
      }
    case 'Hold - Back Office':
      return {
        label: 'Hold - Back Office',
        chipColor: 'warning',
        reason: null,
        comments: statusComment,
        commentsKey: 'holdOps',
      }
    default:
      return null
  }
}
